import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import {
  addSection,
  addSectionsToQuote,
  deleteSection,
  editSection,
  getSections,
} from "./thunk";
import { SectionPayloadData } from "types/section.api";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface SectionStatus {
  fetch: RequestStatus;
  add: RequestStatus;
  edit: RequestStatus;
  delete: RequestStatus;
  addToQuote: RequestStatus;
}

interface SectionErrors {
  fetch: string | null;
  add: string | null;
  edit: string | null;
  delete: string | null;
  addToQuote: string | null;
}

export interface SectionState {
  sections: SectionPayloadData[];
  ids: number[];
  meta: PaginationMeta;
  status: SectionStatus;
  errors: SectionErrors;
}

export const initialState: SectionState = {
  sections: [],
  ids: [],
  meta: {
    current_page: 0,
    from: 0,
    last_page: 0,
    links: [],
    path: "",
    per_page: 0,
    to: 0,
    total: 0,
  },
  status: {
    fetch: "idle",
    add: "idle",
    edit: "idle",
    delete: "idle",
    addToQuote: "idle",
  },
  errors: {
    fetch: null,
    add: null,
    edit: null,
    delete: null,
    addToQuote: null,
  },
};

const sectionsSlice = createSlice({
  name: "section",
  initialState,
  reducers: {
    updateSectionLocally: (
      state,
      action: PayloadAction<{
        sectionId: number;
        updates: Partial<SectionPayloadData>;
      }>
    ) => {
      const { sectionId, updates } = action.payload;
      const index = state.sections.findIndex(
        (section) => section.id === sectionId
      );
      if (index !== -1) {
        state.sections[index] = { ...state.sections[index], ...updates };
        state.sections.sort((a, b) => a.sort - b.sort);
      }
    },
  },
  extraReducers: (builder) => {
    // get sections
    builder.addCase(getSections.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getSections.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<SectionPayloadData>>>
      ) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.sections = action.payload.payload.data.sort(
          (a, b) => a.sort - b.sort
        );
        state.ids = action.payload.payload.data.map((section) => section.id);
        state.meta = action?.payload?.payload?.meta;
      }
    );
    builder.addCase(getSections.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    builder.addCase(addSection.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
    builder.addCase(addSection.fulfilled, (state, action) => {
      state.status.add = "succeeded";
      const addedSection = action.payload.payload;
      state.sections.unshift(addedSection);
      state.sections.sort((a, b) => a.sort - b.sort);
      // state.ids.unshift(addedSection.id);
    });
    builder.addCase(addSection.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    builder.addCase(editSection.pending, (state) => {
      state.status.edit = "loading";
      state.errors.edit = null;
    });
    builder.addCase(editSection.fulfilled, (state, action) => {
      state.status.edit = "succeeded";
      const updatedSection = action.payload.payload;
      const index = state.sections.findIndex(
        (section) => section.id === updatedSection.id
      );
      console.log(updatedSection);
      state.sections[index] = updatedSection;
      state.sections.sort((a, b) => a.sort - b.sort);
    });
    builder.addCase(editSection.rejected, (state, action) => {
      state.status.edit = "failed";
      state.errors.edit = action.payload || null;
    });

    // // delete section
    builder.addCase(deleteSection.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteSection.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedSectionId = action.payload.payload;
        state.sections = state.sections.filter(
          (item) => item.id !== deletedSectionId
        );
        state.ids = state.ids.filter((id) => id !== deletedSectionId);
      }
    );
    builder.addCase(deleteSection.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });

    //Add Sections to Quote
    builder.addCase(addSectionsToQuote.pending, (state) => {
      state.status.addToQuote = "loading";
      state.errors.addToQuote = null;
    });
    builder.addCase(addSectionsToQuote.fulfilled, (state) => {
      state.status.addToQuote = "succeeded";
    });
    builder.addCase(addSectionsToQuote.rejected, (state, action) => {
      state.status.addToQuote = "failed";
      state.errors.addToQuote = action.payload || null;
    });
  },
});

export const { updateSectionLocally } = sectionsSlice.actions;

export default sectionsSlice.reducer;

export const selectSectionsState = (state: any) =>
  state.section as typeof initialState;
